import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import isEqual from 'lodash/isEqual';

import CustomModal from '../../../misc/Modal';
import TiptapCompactEditor from '../rte/TiptapCompactEditor';
import ThemeIcons from '../../../../theme/Icons';
import FieldTypes from '../../../../constants/FieldTypes';
import ImageField from '../ImageField';
import SignatureField from '../SignatureField';
import BarcodeGenericField from '../BarcodeGenericField';
import BlockGenericField from '../BlockGenericField';
import FieldDateInput from '../FieldDateInput';

const isCellValueFilled = (value) => {

  if (value == null) return false;

  if (typeof value === 'string') return value !== '';

  if (Array.isArray(value)) return value.length > 0;

  return true;

};

const fileToDataUri = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

let _pendingIdCounter = 0;
const generatePendingId = () => `_pending_${Date.now()}_${++_pendingIdCounter}`;

// ===  Styled Components  ===

const StyledColumnSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 12px 4px;
`;

const StyledImageFieldWrapper = styled.div`
  max-height: 220px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledSignatureFieldWrapper = styled.div`
  height: 150px;
`;

const StyledSectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const StyledFieldGroup = styled.div`
  margin-bottom: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledFieldLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #666;
  margin-bottom: 4px;
`;

const StyledEditorWrapper = styled.div`
  min-height: 30px;

  .tiptap.ProseMirror {
    min-height: 24px;
    padding: 4px 6px;
    font-size: 13px;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;

  &:focus {
    border-color: #2196f3;
  }
`;

const StyledSelect = styled.select`
  width: 100%;
  box-sizing: border-box;
  padding: 6px 8px;
  font-size: 13px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  outline: none;
  background: #fff;

  &:focus {
    border-color: #2196f3;
  }
`;

const StyledCheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
`;

const StyledCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 0;
  border-bottom: 1px solid #e0e0e0;

  &:last-child {
    border-bottom: none;
  }
`;

// ===  Fill-mode cell editor per column type  ===

const FillCellEditor = ({ column, value, timezone, onChange }) => {
  const colType = column.type;

  if (colType === 'dropdown') {
    const options = (column.options || []).filter((o) => !o.deleted);
    return (
      <StyledSelect
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
      >
        <option value="" />
        {options.map((opt) => (
          <option key={opt._id} value={opt._id}>{opt.value}</option>
        ))}
      </StyledSelect>
    );
  }

  if (colType === 'multiSelect') {
    const options = (column.options || []).filter((o) => !o.deleted);
    const selected = Array.isArray(value) ? value : [];
    const handleToggle = (optId) => {
      const next = selected.includes(optId)
        ? selected.filter((id) => id !== optId)
        : [...selected, optId];
      onChange(next);
    };
    return (
      <StyledCheckboxGroup>
        {options.map((opt) => (
          <StyledCheckboxLabel key={opt._id}>
            <input
              type="checkbox"
              checked={selected.includes(opt._id)}
              onChange={() => handleToggle(opt._id)}
            />
            {opt.value}
          </StyledCheckboxLabel>
        ))}
      </StyledCheckboxGroup>
    );
  }

  if (colType === 'number') {
    return (
      <StyledInput
        type="number"
        value={value != null ? value : ''}
        onChange={(e) => {
          const val = e.target.value;
          if (val === '') {
            onChange('');
          } else {
            onChange(val.indexOf('.') === -1 ? parseInt(val, 10) : parseFloat(val));
          }
        }}
      />
    );
  }

  if (colType === 'date') {
    return (
      <FieldDateInput
        disabled={false}
        value={value}
        timezone={timezone}
        enableTimezoneAnchor={true}
        format={column.format || 'MM/DD/YYYY'}
        primaryColor="#2196f3"
        fontSize={13}
        fontColor="#444"
        borderColor="#e0e0e0"
        borderRadius={4}
        borderWidth={1}
        backgroundColor="#fff"
        padding={6}
        onChange={onChange}
      />
    );
  }

  if (colType === 'text') {
    const displayValue = value != null ? String(value) : '';
    return (
      <StyledInput
        type="text"
        value={displayValue}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  }

  const displayValue = value != null ? String(value) : '';
  return (
    <StyledInput
      type="text"
      value={displayValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

FillCellEditor.propTypes = {
  column: PropTypes.object.isRequired,
  value: PropTypes.any,
  timezone: PropTypes.string,
  onChange: PropTypes.func.isRequired,
};

// ===  Main Modal Component  ===

const TableFieldRteEditRowModal = ({
  isOpen,
  rteColumns,
  row,
  tableColumns,
  fieldPositionTableColumns,
  fieldMode,
  onRteSettingsBatchUpdate,
  onCellChange,
  onCellBatchChange,
  onCellFileUpload,
  onCellFileBatchUpload,
  onCellFileClick,
  onCellFileDelete,
  onCapture,
  enableSystemFilePicker,
  onClose,
}) => {

  const [showConfirm, setShowConfirm] = useState(false);
  const pendingRef = useRef({});
  const initFiredRef = useRef(new Set());
  const prevRowCellsRef = useRef({});
  const [draftCells, setDraftCells] = useState({});
  const [draftTimezone, setDraftTimezone] = useState(null);
  const draftSignerRef = useRef(null);
  const pendingUploadsRef = useRef({});

  // ===  Fill-mode: pending cell changes  ===

  useEffect(() => {
    if (!isOpen || fieldMode !== 'fill') return;
    prevRowCellsRef.current = row?.cells ? { ...row.cells } : {};
    setDraftCells(row?.cells ? { ...row.cells } : {});
    setDraftTimezone(row?.tz || null);
  }, [fieldMode, isOpen, row?._id, row?.tz]);

  useEffect(() => {
    if (!isOpen || fieldMode !== 'fill' || !row?.cells) return;

    setDraftCells((currentDraftCells) => {
      const previousRowCells = prevRowCellsRef.current || {};
      const nextDraftCells = { ...currentDraftCells };
      let changed = false;

      Object.keys(row.cells).forEach((columnId) => {
        if (!isEqual(previousRowCells[columnId], row.cells[columnId])) {
          nextDraftCells[columnId] = row.cells[columnId];
          changed = true;
        }
      });

      prevRowCellsRef.current = { ...row.cells };

      return changed ? nextDraftCells : currentDraftCells;
    });
  }, [fieldMode, isOpen, row?.cells]);

  const draftCellsRef = useRef(draftCells);

  draftCellsRef.current = draftCells;

  const hasUnfilledRequired = useMemo(() => {

    if (fieldMode !== 'fill') return false;

    const requiredColumns = (tableColumns || []).filter((col) => col.required && !col.deleted);

    if (requiredColumns.length === 0) return false;

    return requiredColumns.some((col) => !isCellValueFilled(draftCells[col._id]));

  }, [fieldMode, tableColumns, draftCells]);

  const hasPendingChanges = useCallback(() => {

    if (fieldMode === 'fill') {
      const hasPendingUploads = Object.keys(pendingUploadsRef.current).length > 0;
      return hasPendingUploads || !isEqual(draftCellsRef.current, row?.cells || {});
    }

    return Object.keys(pendingRef.current).length > 0;

  }, [fieldMode, row?.cells]);

  // ===  Edit-mode handlers (existing)  ===

  const handleEditorUpdate = useCallback((settingId, fieldKey, json) => {
    const trackingKey = `${settingId}_${fieldKey}`;
    if (!initFiredRef.current.has(trackingKey)) {
      initFiredRef.current.add(trackingKey);
      return;
    }
    const existing = pendingRef.current[settingId] || {};
    pendingRef.current[settingId] = { ...existing, [fieldKey]: json };
  }, []);

  const resetState = useCallback(() => {
    pendingRef.current = {};
    initFiredRef.current = new Set();
    prevRowCellsRef.current = row?.cells ? { ...row.cells } : {};
    draftSignerRef.current = null;
    pendingUploadsRef.current = {};
    setDraftCells(row?.cells ? { ...row.cells } : {});
    setDraftTimezone(row?.tz || null);
  }, [row?.cells, row?.tz]);

  // ===  Fill-mode cell change handler  ===

  const handleFillCellChange = useCallback((columnId, value, timezone) => {
    setDraftCells((currentDraftCells) => ({
      ...currentDraftCells,
      [columnId]: value,
    }));
    if (timezone) setDraftTimezone(timezone);
  }, []);

  // ===  Fill-mode image upload handler (deferred)  ===

  const handleImageFileUpload = useCallback(async (colId, multi, files) => {

    const fileList = Array.isArray(files) ? files : [files];

    const tempUrlObjects = [];

    for (let i = 0; i < fileList.length; i++) {
      const file = fileList[i];
      const dataUri = await fileToDataUri(file);
      tempUrlObjects.push({
        _id: generatePendingId(),
        url: dataUri,
        fileName: file.name,
        _pending: true,
      });
    }

    if (multi) {
      const existing = pendingUploadsRef.current[colId] || [];
      pendingUploadsRef.current[colId] = [...existing, ...fileList];
    } else {
      pendingUploadsRef.current[colId] = [fileList[0]];
    }

    setDraftCells((currentDraftCells) => {
      const existingValue = currentDraftCells[colId];

      let nextValue;

      if (multi) {
        const existingArray = Array.isArray(existingValue) ? existingValue : [];
        nextValue = [...existingArray, ...tempUrlObjects];
      } else {
        nextValue = [tempUrlObjects[0]];
      }

      return { ...currentDraftCells, [colId]: nextValue };
    });

  }, []);

  const handleImageFileDelete = useCallback((colId, multi, urlObject, urlIndex) => {

    if (urlObject && urlObject._pending) {

      const pendingFiles = pendingUploadsRef.current[colId];

      if (pendingFiles) {

        setDraftCells((currentDraftCells) => {
          const existingValue = Array.isArray(currentDraftCells[colId]) ? currentDraftCells[colId] : [];
          const pendingItems = existingValue.filter((item) => item._pending);
          const pendingIdx = pendingItems.findIndex((item) => item._id === urlObject._id);

          if (pendingIdx !== -1 && pendingFiles.length > pendingIdx) {
            const nextFiles = [...pendingFiles];
            nextFiles.splice(pendingIdx, 1);

            if (nextFiles.length === 0) {
              delete pendingUploadsRef.current[colId];
            } else {
              pendingUploadsRef.current[colId] = nextFiles;
            }
          }

          const nextValue = existingValue.filter((item) => item._id !== urlObject._id);
          return { ...currentDraftCells, [colId]: nextValue };
        });

      }

      return;
    }

    setDraftCells((currentDraftCells) => {
      const existingValue = Array.isArray(currentDraftCells[colId]) ? currentDraftCells[colId] : [];
      const nextValue = [...existingValue];
      nextValue.splice(urlIndex, 1);
      return { ...currentDraftCells, [colId]: nextValue };
    });

  }, []);

  // ===  Save  ===

  const handleSave = useCallback(() => {

    if (!hasPendingChanges()) {
      resetState();
      onClose();
      return;
    }

    if (fieldMode === 'fill') {

      const nextCells = draftCellsRef.current || {};

      const currentCells = row?.cells || {};

      const changedIds = Array.from(new Set([...Object.keys(currentCells), ...Object.keys(nextCells)]));

      const nextTimezone = draftTimezone || row?.tz;

      const pendingUploadColumnIds = Object.keys(pendingUploadsRef.current);

      const batchedCells = {};

      let hasDateColumn = false;

      for (let i = 0; i < changedIds.length; i++) {

        const colId = changedIds[i];

        if (pendingUploadColumnIds.includes(colId)) continue;

        if (!isEqual(nextCells[colId], currentCells[colId])) {

          const cellValue = nextCells[colId];
          const cleanedValue = Array.isArray(cellValue)
            ? cellValue.filter((item) => !item?._pending)
            : cellValue;

          batchedCells[colId] = cleanedValue;
          const column = (tableColumns || []).find((nextColumn) => nextColumn._id === colId);
          if (column?.type === 'date') hasDateColumn = true;
        }

      }

      const timezone = hasDateColumn ? nextTimezone : undefined;
      const signer = draftSignerRef.current || undefined;

      if (pendingUploadColumnIds.length > 0) {

        const columnUploads = {};

        pendingUploadColumnIds.forEach((colId) => {
          const draftValue = nextCells[colId];
          const existingValue = Array.isArray(draftValue)
            ? draftValue.filter((item) => !item?._pending)
            : [];

          columnUploads[colId] = {
            files: pendingUploadsRef.current[colId],
            existingValue,
          };
        });

        if (onCellFileBatchUpload) {
          onCellFileBatchUpload(row._id, columnUploads, () => {}, () => {}, batchedCells, timezone, signer);
        } else if (onCellFileUpload) {
          if (Object.keys(batchedCells).length > 0) {
            if (onCellBatchChange) {
              onCellBatchChange(row._id, batchedCells, timezone, signer);
            }
          }
          pendingUploadColumnIds.forEach((colId) => {
            onCellFileUpload(row._id, colId, columnUploads[colId].files, () => {}, () => {});
          });
        }

      } else if (Object.keys(batchedCells).length > 0) {

        if (onCellBatchChange) {
          onCellBatchChange(row._id, batchedCells, timezone, signer);
        } else if (onCellChange) {
          Object.keys(batchedCells).forEach((colId) => {
            const column = (tableColumns || []).find((nextColumn) => nextColumn._id === colId);
            if (column?.type === 'date') {
              onCellChange(row._id, colId, batchedCells[colId], nextTimezone, signer);
            } else {
              onCellChange(row._id, colId, batchedCells[colId], undefined, signer);
            }
          });
        }

      }

    } else {
      onRteSettingsBatchUpdate(pendingRef.current);
    }

    resetState();

    onClose();

  }, [hasPendingChanges, draftTimezone, fieldMode, onRteSettingsBatchUpdate, onCellChange, onCellBatchChange, onCellFileUpload, onCellFileBatchUpload, onClose, resetState, row, tableColumns]);

  // ===  Close / Discard  ===

  const handleCloseAttempt = useCallback(() => {

    if (hasPendingChanges()) {
      setShowConfirm(true);
    } else {
      resetState();
      onClose();
    }

  }, [hasPendingChanges, onClose, resetState]);

  const handleDiscard = useCallback(() => {
    resetState();
    setShowConfirm(false);
    onClose();
  }, [onClose, resetState]);

  const handleConfirmSave = useCallback(() => {
    setShowConfirm(false);
    handleSave();
  }, [handleSave]);

  // ===  Render  ===

  const activeColumns = useMemo(
    () => (tableColumns || []).filter((col) => !col.deleted),
    [tableColumns]
  );

  const renderNativeField = (column, cellValue) => {
    const colType = column.type;
    const colId = column._id;
    const rowId = row?._id;

    if (colType === 'image') {
      return (
        <StyledImageFieldWrapper>
          <ImageField
            type={FieldTypes.image}
            multi={column.multi}
            rteEmbedded
            disabled={false}
            value={cellValue}
            fieldId={colId}
            enableSystemFilePicker={enableSystemFilePicker}
            primaryMaxWidth={column.maxImageWidth || 200}
            primaryMaxHeight={column.maxImageHeight || 200}
            dangerColor="#e74c3c"
            borderWidth={1}
            borderColor="#e0e0e0"
            borderRadius={4}
            onFileUpload={(f, i, p, files, loadingCallback, errorCallback) => {
              loadingCallback(true);
              handleImageFileUpload(colId, column.multi, files).then(() => {
                loadingCallback(false);
              }).catch(() => {
                loadingCallback(false);
                if (errorCallback) errorCallback(true);
              });
            }}
            onFileClick={(f, i, p, urlObject, urlIndex) => {
              if (onCellFileClick) onCellFileClick(rowId, colId, urlObject, urlIndex);
            }}
            onFileDelete={(f, i, p, urlObject, urlIndex) => {
              handleImageFileDelete(colId, column.multi, urlObject, urlIndex);
            }}
            onChange={(f, i, p, value) => {
              handleFillCellChange(colId, value);
            }}
            padding={2}
          />
        </StyledImageFieldWrapper>
      );
    }

    if (colType === 'signature') {
      return (
        <StyledSignatureFieldWrapper>
          <SignatureField
            type={colType}
            fieldId={colId}
            disabled={false}
            value={cellValue}
            borderWidth={1}
            borderColor="#e0e0e0"
            borderRadius={4}
            onCapture={onCapture}
            onChange={(f, i, p, value, signer) => {
              handleFillCellChange(colId, value);
              if (signer) draftSignerRef.current = signer;
            }}
            maxImageWidth={column.maxImageWidth}
            maxImageHeight={column.maxImageHeight}
          />
        </StyledSignatureFieldWrapper>
      );
    }

    if (colType === 'barcode') {
      return (
        <BarcodeGenericField
          type={colType}
          fieldId={colId}
          disabled={false}
          value={cellValue}
          wrapperStyles={{ height: 'auto' }}
          borderWidth={1}
          borderColor="#e0e0e0"
          borderRadius={4}
          onCapture={onCapture}
          onChange={(f, i, p, value) => {
            handleFillCellChange(colId, value);
          }}
        />
      );
    }

    if (colType === 'block') {
      return (
        <BlockGenericField
          type={colType}
          fieldId={colId}
          disabled={false}
          fieldMode={fieldMode}
          value={cellValue}
          borderWidth={1}
          borderColor="#e0e0e0"
          borderRadius={4}
          onChange={(f, i, p, value) => {
            handleFillCellChange(colId, value);
          }}
        />
      );
    }

    return null;
  };

  const isNativeFieldType = (colType) => {
    return colType === 'image' || colType === 'signature' || colType === 'barcode' || colType === 'block';
  };

  const renderFillModeContent = () => {
    return activeColumns.map((column) => {
      const colId = column._id;
      const cellValue = draftCells[colId];
      const fieldPositionColumn = fieldPositionTableColumns && fieldPositionTableColumns[colId] ? fieldPositionTableColumns[colId] : {};
      const resolvedColumn = fieldPositionColumn.format ? { ...column, format: fieldPositionColumn.format } : column;

      if (isNativeFieldType(column.type)) {
        return (
          <StyledColumnSection key={colId}>
            <StyledSectionLabel>
              {column.title || colId}
              {column.required ? <span style={{ color: 'red', fontSize: '16px', verticalAlign: 'middle', lineHeight: 1 }}> *</span> : null}
            </StyledSectionLabel>
            <StyledFieldGroup>
              {renderNativeField(column, cellValue)}
            </StyledFieldGroup>
          </StyledColumnSection>
        );
      }

      return (
        <StyledColumnSection key={colId}>
          <StyledSectionLabel>
            {column.title || colId}
            {column.required ? <span style={{ color: 'red', fontSize: '16px', verticalAlign: 'middle', lineHeight: 1 }}> *</span> : null}
          </StyledSectionLabel>
          <StyledFieldGroup>
            <FillCellEditor
              column={resolvedColumn}
              value={cellValue}
              timezone={draftTimezone || row?.tz}
              onChange={(val, timezone) => handleFillCellChange(colId, val, timezone)}
            />
          </StyledFieldGroup>
        </StyledColumnSection>
      );
    });
  };

  const renderEditModeContent = () => {
    return (rteColumns || []).map((column, index) => {
      return (
        <StyledColumnSection key={column._id}>
          <StyledSectionLabel>Column {index + 1}</StyledSectionLabel>
          <StyledFieldGroup>
            <StyledFieldLabel>Title</StyledFieldLabel>
            <StyledEditorWrapper>
              <TiptapCompactEditor
                content={column.title || ''}
                disabled={false}
                onUpdate={(json) => handleEditorUpdate(column._id, 'title', json)}
                borderColor="#e0e0e0"
                borderRadius={4}
                borderWidth={1}
                padding={4}
                fontSize={13}
              />
            </StyledEditorWrapper>
          </StyledFieldGroup>
          <StyledFieldGroup>
            <StyledFieldLabel>Value</StyledFieldLabel>
            <StyledEditorWrapper>
              <TiptapCompactEditor
                content={column.value || ''}
                disabled={false}
                onUpdate={(json) => handleEditorUpdate(column._id, 'value', json)}
                borderColor="#e0e0e0"
                borderRadius={4}
                borderWidth={1}
                padding={4}
                fontSize={13}
              />
            </StyledEditorWrapper>
          </StyledFieldGroup>
        </StyledColumnSection>
      );
    });
  };

  if (!isOpen && !showConfirm) return null;

  return (
    <>
      <CustomModal
        icon={ThemeIcons.settings.edit}
        title="Edit Row"
        isOpen={isOpen}
        width={700}
        height={600}
        submitText="Save"
        submitDisabled={hasUnfilledRequired}
        onSubmit={handleSave}
        cancelText="Close"
        onCancel={handleCloseAttempt}
        onHide={handleCloseAttempt}
      >
        {fieldMode === 'fill' ? renderFillModeContent() : renderEditModeContent()}
      </CustomModal>
      <CustomModal
        title="Unsaved Changes"
        isOpen={showConfirm}
        width={340}
        height={120}
        submitText="Save"
        onSubmit={handleConfirmSave}
        cancelText="Discard"
        onCancel={handleDiscard}
        onHide={handleDiscard}
      >
        <span>You have unsaved changes. Would you like to save or discard them?</span>
      </CustomModal>
    </>
  );

};

TableFieldRteEditRowModal.propTypes = {
  isOpen: PropTypes.bool,
  rteColumns: PropTypes.array,
  row: PropTypes.object,
  tableColumns: PropTypes.array,
  fieldPositionTableColumns: PropTypes.object,
  fieldMode: PropTypes.string,
  onRteSettingsBatchUpdate: PropTypes.func,
  onCellChange: PropTypes.func,
  onCellBatchChange: PropTypes.func,
  onCellFileUpload: PropTypes.func,
  onCellFileBatchUpload: PropTypes.func,
  onCellFileClick: PropTypes.func,
  onCellFileDelete: PropTypes.func,
  onCapture: PropTypes.func,
  enableSystemFilePicker: PropTypes.bool,
  onClose: PropTypes.func,
};

export default TableFieldRteEditRowModal;
